import { Link } from "react-router-dom";
import type { CSSProperties, ReactNode } from "react";
import logoCf from "../assets/cf-logo-branco.png";
import logoCfFbc from "../assets/logo-cf-fbc-branco.png";
import simboloCf from "../assets/cf-simbolo.png";
import escudoFbc from "../assets/fbc-escudo.png";
import escudoFbcBranco from "../assets/fbc-escudo-branco.png";
import { supabase } from "../lib/supabase";
import { useTema, type Tema } from "../lib/tema";
import { diasAte, formatarData, iniciais, type Vigencia } from "../lib/dados";
import { registrarAbertura } from "../lib/uso";
import { useOperador } from "../lib/admin";
import { useTemConsultoria } from "../lib/consultoria";

// Peças visuais compartilhadas pelas telas do aluno. A linguagem é a do
// `design-system-cf`: painel azul quadriculado, cards claros elevados, CTA pill
// laranja com texto azul escuro, e a "porta" — todo destino navegável é um card
// inteiro clicável com a soleira laranja acendendo no hover.

export function LogoCF({ className = "h-7" }: { className?: string }) {
  return <img src={logoCf} alt="Contabilidade Facilitada" className={`${className} w-auto`} />;
}

/**
 * A marca do header: Contabilidade Facilitada **e** FBC, lado a lado.
 *
 * Não é o logo do CF com o selo FBC pendurado do lado: é o lockup oficial das
 * duas marcas, com o divisor e o escudo da faculdade, na versão branca — feita
 * para fundo azul, que é o do header. Veio de
 * `documentos-secretaria/src/assets/logo-cf-fbc-white.png`, outro produto da
 * casa; o pacote `design-system-cf` só traz o logo do CF isolado, e os vetores
 * das duas marcas estão no Figma sob permissão.
 *
 * Some no celular: com 1600×209 de proporção, na altura de header o lockup
 * inteiro fica ilegível numa tela de 390px. Lá entra o logo do CF sozinho — a
 * FBC continua nomeada por extenso na área da faculdade.
 */
export function LogoCfFbc({ className = "h-8" }: { className?: string }) {
  return (
    <>
      <img
        src={logoCfFbc}
        alt="Contabilidade Facilitada e FBC — Faculdade Brasileira de Contabilidade"
        className={`hidden w-auto sm:block ${className}`}
      />
      <img src={logoCf} alt="Contabilidade Facilitada" className="h-6 w-auto sm:hidden" />
    </>
  );
}

/**
 * Selo de vigência: um componente, três estados.
 *
 * Vitalício é DATA NULA, nunca data grande — data grande no legado é dado sujo.
 * Quando falta pouco, o selo fica laranja; o aviso aparece uma vez só por tela,
 * porque repetido vira pressão de venda.
 */
export function SeloVigencia({ vigencia, prefixo }: { vigencia: Vigencia; prefixo?: string }) {
  const base = "inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold tabular-nums whitespace-nowrap";

  if (vigencia.vitalicio) {
    return (
      <span className={`${base} bg-tile text-link`}>
        {prefixo ? `${prefixo} é ` : ""}Vitalício <span aria-hidden="true">∞</span>
      </span>
    );
  }

  if (!vigencia.fim_efetivo) {
    // Não vitalício e sem data não deveria existir na view; se existir, o selo
    // cala em vez de mostrar "até —".
    return null;
  }

  const dias = diasAte(vigencia.fim_efetivo);
  if (dias <= 30) {
    return (
      <span className={`${base} bg-alerta-fundo text-alerta-tinta`}>
        {dias <= 1 ? "vence hoje" : `vence em ${dias} dias`}
      </span>
    );
  }

  // O prefixo entra ANTES do "até", não no lugar dele: "Seu acesso: 12/08/2027"
  // lia como data de hoje; "Seu acesso vale até 12/08/2027" diz o que é.
  return (
    <span className={`${base} bg-tile text-titulo`}>
      {prefixo ? `${prefixo} até ` : "até "}
      {formatarData(vigencia.fim_efetivo)}
    </span>
  );
}

/** Tile de ícone. `laranja` para o que é marca; azul para o resto. */
export function Tile({ children, cor = "azul" }: { children: ReactNode; cor?: "azul" | "laranja" | "navy" }) {
  const cores = {
    azul: "bg-tile text-link",
    laranja: "bg-tile text-laranja-texto",
    navy: "bg-laranja/15 text-laranja-claro",
  };
  return (
    <span className={`grid size-11 shrink-0 place-items-center rounded-[13px] ${cores[cor]}`}>
      {children}
    </span>
  );
}

/**
 * Card de curso trancado por contrato pendente (000044).
 *
 * Chip próprio, e não o "Em breve": os dois estados chegam ao front do mesmo
 * jeito (a view anula a `url`), mas dizem coisas opostas. "Em breve" é promessa
 * do CF; "Assine para liberar" é ação do aluno. Confundir os dois faz quem pode
 * resolver ficar esperando.
 */
export function ChipTrancado({ sobreNavy = false }: { sobreNavy?: boolean }) {
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${
        sobreNavy ? "bg-laranja/20 text-laranja-claro" : "bg-alerta-fundo text-alerta-tinta"
      }`}
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" className="size-3" aria-hidden="true">
        <rect x="4" y="11" width="16" height="10" rx="2" />
        <path d="M8 11V8a4 4 0 0 1 8 0v3" />
      </svg>
      Assine para liberar
    </span>
  );
}

/**
 * O chip do card sem link: por que ele não abre.
 *
 * Um componente só porque os dois motivos chegam iguais ao front — a view anula
 * `url` e devolve `destino = 'em-breve'` nos dois casos — e só o campo
 * `trancado` os separa. Deixar a escolha em cada tela era repetir um ternário em
 * cinco lugares e esquecer no sexto.
 */
export function ChipDoCard({
  card,
  sobreNavy = false,
}: {
  card: { trancado?: boolean };
  sobreNavy?: boolean;
}) {
  return card.trancado ? <ChipTrancado sobreNavy={sobreNavy} /> : <ChipEmBreve sobreNavy={sobreNavy} />;
}

export function ChipEmBreve({ sobreNavy = false }: { sobreNavy?: boolean }) {
  // #4a515c sobre o cinza-claro do chip dá 5,99:1; o cinza da marca (#5d6673)
  // dava 4,35:1 e reprovava assim que o card ganhava fundo mais escuro.
  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
        sobreNavy ? "bg-navy-ink/10 text-navy-mute" : "bg-chip text-chip-tinta"
      }`}
    >
      Em breve
    </span>
  );
}

/**
 * Aparência do card cujo destino ainda não existe (`destino = 'em-breve'`).
 *
 * Não usa `opacity`: opacidade no container compõe o texto junto com o fundo
 * contra a página, e a descrição caía para 2,88:1 — abaixo do mínimo de 4,5:1.
 * Quando isto foi escrito, `url` era nula em 31 dos 31 cards e esse era o estado
 * de **toda** a prateleira, justamente onde as descrições novas foram escritas
 * para serem lidas. A 000062 abriu quatro; os outros 27 continuam aqui. O "em
 * breve" é dito pelo chip e pela ausência de link, não apagando o texto.
 */
export const CARD_EM_BREVE = "bg-superficie-2";

/**
 * ANTES DE TIRAR O `width`/`height` DAQUI, LEIA.
 *
 * O `className` tem `size-4` como padrão, e padrão de parâmetro é SUBSTITUÍDO, não
 * somado: quem chama com `className="shrink-0 text-cinza"` fica com um SVG sem
 * tamanho nenhum. SVG com `viewBox` e sem dimensão ESTICA para preencher o pai — e
 * foi assim que a tela inicial do IR Pro saiu com 34 setas gigantes, uma por card,
 * numa página de 8.200 pixels de altura. Victor viu antes de mim.
 *
 * `width="1em" height="1em"` dá dimensão intrínseca ao ícone: qualquer `size-*` no
 * className continua vencendo por CSS, e a ausência de classe agora vale "do tamanho
 * da letra" em vez de "do tamanho do pai". A armadilha deixa de existir para todos
 * os 13 lugares que usam este ícone.
 */
export function SetaDireita({ className = "size-4" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <path d="M5 12h14M12 5l7 7-7 7" />
    </svg>
  );
}

/**
 * Ícone de link externo — e o aviso em texto para quem não vê o ícone.
 *
 * Todos os destinos do hub (aulas no Memberkit, comunidade no Fórum, satélites)
 * abrem em outra aba. Sem o aviso, quem usa leitor de tela descobre a mudança de
 * contexto só depois que ela acontece.
 */
export function IconeExterno({ className = "size-4" }: { className?: string }) {
  return (
    <>
      {/* `width`/`height` pelo mesmo motivo da SetaDireita — ver o comentário lá. */}
      <svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
        <path d="M15 3h6v6" />
        <path d="M10 14 21 3" />
      </svg>
      <span className="sr-only">(abre em outra aba)</span>
    </>
  );
}

// ---------------------------------------------------------------------------
// Formulário — as três peças que toda tela de escrita do hub usa
// ---------------------------------------------------------------------------
// Nasceram dentro de `Perfil.tsx`, a primeira tela em que o aluno escreveu, e
// subiram para cá quando a segunda apareceu (IR Proposta) e a terceira nasceu
// pública (o formulário de orçamento). Três cópias de um campo de input divergem
// no foco, e foco divergente é acessibilidade quebrada em uma das telas.

/**
 * O input do hub SEM largura: para quem vive num `flex-wrap` e precisa da sua
 * própria (`w-40`, `flex-1`). Existe porque `CAMPO` carrega `w-full`, e o
 * `w-full` vence qualquer `w-*` que se escreva ao lado — a ordem no CSS gerado
 * decide, não a ordem na string. Foi assim que as duas datas da barra de
 * filtros do balcão saíram ocupando a linha inteira cada uma (09/09).
 */
export const CAMPO_BASE =
  "h-12 rounded-xl border-[1.5px] border-borda bg-superficie px-4 text-tinta transition placeholder:text-cinza/60 focus:border-laranja focus:ring-3 focus:ring-laranja/20 focus:outline-none";

/** O input do hub: 48px de altura (alvo de toque), foco laranja com halo. */
export const CAMPO = `${CAMPO_BASE} w-full`;

/**
 * O campo de busca das telas de administração — a LUPA dentro do campo, numa
 * linha só. Pedido de Victor em 09/09/2026, olhando o balcão da secretaria: a
 * busca era um `<input>` com um botão "Buscar" ao lado, que quebrava em duas
 * linhas a 1280px e morava longe dos outros filtros.
 *
 * Dois modos, e a diferença é quem dispara a consulta:
 *
 *   · **com `aoBuscar`** — a lupa é o BOTÃO de enviar (o "Buscar" que virou
 *     ícone), e o campo vive num `<form>`: Enter e o clique fazem o mesmo. É o
 *     modo do balcão, onde a fila tem 12 mil itens e consultar a cada tecla
 *     seria uma consulta por caractere (o comentário de `SuporteFaculdade`).
 *   · **sem `aoBuscar`** — a lupa é só desenho (`aria-hidden`), porque quem
 *     consulta é o `debounce` de quem chama (Notas, Matrículas, Radar). Ela
 *     está aqui para o campo ter a MESMA cara nas três sub-abas.
 *
 * O que a lupa-botão NÃO pode perder: o estado de "buscando". Antes ele era o
 * texto do botão ("Buscando…"), e um ícone não fala — então `buscando` troca a
 * lupa por um giro e desliga o botão, e quem chama escreve a palavra onde o
 * operador lê o total (a linha "N esperando atendimento"). A ausência de uma
 * peça de estado é a cicatriz do "Limite com a prorrogação": o olho lê o
 * silêncio como "está tudo bem".
 *
 * `aoLimpar` desenha o × à direita quando há texto. Ele existe porque limpar o
 * campo com Backspace não desfaz a busca no modo `aoBuscar` — a lista continua
 * a da última consulta até o próximo Enter — e o × faz as duas coisas.
 */
export function CampoDeBusca({
  valor,
  aoMudar,
  placeholder,
  rotulo,
  aoBuscar,
  buscando = false,
  aoLimpar,
  id,
  className = "",
  ...resto
}: {
  valor: string;
  aoMudar: (v: string) => void;
  placeholder: string;
  /** O nome acessível do campo — não há rótulo visível na barra de filtros. */
  rotulo: string;
  aoBuscar?: () => void;
  buscando?: boolean;
  aoLimpar?: () => void;
  id?: string;
  className?: string;
} & Record<`data-${string}`, string | boolean | undefined>) {
  const dataAttrs = Object.fromEntries(Object.entries(resto).filter(([k]) => k.startsWith("data-")));
  const lupa = (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="size-5" aria-hidden="true">
      <circle cx="11" cy="11" r="7" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
  const giro = (
    <span className="block size-4 animate-spin rounded-full border-2 border-current border-t-transparent" aria-hidden="true" />
  );

  const campo = (
    <div className={`relative ${className}`}>
      {aoBuscar ? (
        <button
          type="submit"
          aria-label={buscando ? "Buscando…" : "Buscar"}
          title="Buscar (Enter)"
          disabled={buscando}
          data-buscar
          className="absolute inset-y-1 left-1 grid w-10 place-items-center rounded-lg text-cinza transition hover:text-laranja-texto focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-laranja/40 disabled:opacity-60"
        >
          {buscando ? giro : lupa}
        </button>
      ) : (
        <span className="pointer-events-none absolute inset-y-0 left-1 grid w-10 place-items-center text-cinza" aria-hidden="true">
          {lupa}
        </span>
      )}
      <input
        id={id}
        type="text"
        role="searchbox"
        enterKeyHint="search"
        autoComplete="off"
        value={valor}
        onChange={(e) => aoMudar(e.target.value)}
        placeholder={placeholder}
        aria-label={rotulo}
        {...dataAttrs}
        className={`${CAMPO} pl-11 ${aoLimpar && valor ? "pr-10" : ""}`}
      />
      {aoLimpar && valor && (
        <button
          type="button"
          onClick={aoLimpar}
          aria-label="Limpar a busca"
          title="Limpar a busca"
          data-limpar-busca
          className="absolute inset-y-1 right-1 grid w-8 place-items-center rounded-lg text-cinza transition hover:text-tinta focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-laranja/40"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="size-4" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      )}
    </div>
  );

  if (!aoBuscar) return campo;
  return (
    <form
      role="search"
      onSubmit={(e) => {
        e.preventDefault();
        aoBuscar();
      }}
      className="contents"
    >
      {campo}
    </form>
  );
}

/** Cartão de seção. */
export function Secao({
  titulo,
  descricao,
  acao,
  children,
  i = 1,
}: {
  titulo: string;
  descricao?: string;
  /** Botão ou link à direita do título — "+ Nova proposta", "Rotacionar link". */
  acao?: ReactNode;
  children: ReactNode;
  i?: number;
}) {
  return (
    <section className="surface rise p-6 sm:p-7" style={{ "--i": i } as CSSProperties}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-bold text-titulo">{titulo}</h2>
          {descricao && <p className="mt-1 text-sm text-cinza">{descricao}</p>}
        </div>
        {acao}
      </div>
      <div className="mt-5">{children}</div>
    </section>
  );
}

/**
 * O recado depois de uma ação.
 *
 * `role="alert"` no erro e `role="status"` no resto: erro interrompe quem usa leitor
 * de tela, confirmação não. Trocar um pelo outro faz o aluno perder o aviso que
 * importa ou ser interrompido por um "salvo" que ele já esperava.
 */
export function Recado({ tipo, texto }: { tipo: "ok" | "erro"; texto: string }) {
  return (
    <p
      role={tipo === "erro" ? "alert" : "status"}
      className={`mt-4 rounded-xl p-3.5 text-sm leading-relaxed ${
        tipo === "erro" ? "bg-erro-fundo text-erro-tinta" : "bg-aviso-fundo text-aviso-tinta"
      }`}
    >
      {texto}
    </p>
  );
}

/**
 * O sinal de que o card abre — e de PARA ONDE.
 *
 * Seta para destino do hub, ícone de link externo para destino de fora. A
 * distinção não é enfeite: `IconeExterno` carrega um `sr-only` com "(abre em outra
 * aba)", e dizer isso num link que abre na MESMA aba é informação falsa para quem
 * usa leitor de tela — pior que nenhuma.
 */
export function SinalDeAbertura({ interno, className }: { interno: boolean; className?: string }) {
  return interno ? <SetaDireita className={className} /> : <IconeExterno className={className} />;
}

/**
 * O card que abre — em aba nova se o destino é de fora, na mesma aba se é daqui.
 *
 * Existe porque a escolha entre `<a target="_blank">` e `<Link>` estava a ponto de
 * ser um ternário repetido em quatro renderizadores de card (três em Curso, um em
 * Início). O comentário de `aberturaDoCard` já avisava o que isso custa: "uma
 * cópia esquecida agora não é card feio, é porta que não abre" — e com destino
 * interno o modo de falha é pior, porque `<a target="_blank" rel="noreferrer">`
 * sobre uma rota do próprio app FUNCIONA, só que recarregando o bundle numa
 * segunda aba. Ninguém abre um bug assim; o aluno só acha o hub lento.
 *
 * Quem não abre não vira link nenhum: `<div>`, sem href vazio e sem foco de
 * teclado em elemento que não leva a lugar nenhum.
 */
export function LinkDoCard({
  abertura,
  className,
  classeFechado,
  style,
  children,
}: {
  abertura:
    | { abre: false }
    | { abre: true; href: string; interno: boolean; registrar?: { card: string; curso: string | null } };
  className: string;
  /**
   * Classe extra quando o card NÃO abre — é onde entra `CARD_EM_BREVE`. Separada
   * porque o card fechado do curso muda de fundo, e o herói de Aulas não.
   */
  classeFechado?: string;
  style?: CSSProperties;
  children: ReactNode;
}) {
  if (!abertura.abre) {
    return (
      <div className={classeFechado ? `${className} ${classeFechado}` : className} style={style}>
        {children}
      </div>
    );
  }
  if (abertura.interno) {
    return (
      <Link to={abertura.href} className={`${className} porta`} style={style}>
        {children}
      </Link>
    );
  }
  // O clique no card `url` é registrado ANTES de a aba nova abrir, sem esperar
  // resposta (000303): o `<a target="_blank">` segue o caminho dele, e a falha
  // do registro não pode segurar o aluno.
  const registrar = abertura.registrar;
  return (
    <a
      href={abertura.href}
      target="_blank"
      rel="noreferrer"
      className={`${className} porta`}
      style={style}
      onClick={registrar ? () => registrarAbertura(registrar.card, registrar.curso) : undefined}
    >
      {children}
    </a>
  );
}

/** Barra do topo: azul CF, com o item ativo em pill laranja. */
export function Topo({
  atual,
  nome,
  avatarUrl,
}: {
  atual: "inicio" | "faculdade";
  nome: string | null;
  avatarUrl?: string | null;
}) {
  // `min-h-11` = 44px, o mínimo de alvo de toque. Com `py-2` o item ficava em
  // 36px, e o público que mais usa o hub no celular é o que menos acerta alvo
  // pequeno.
  const item = (chave: "inicio" | "faculdade", rotulo: string, para: string) => (
    <Link
      to={para}
      aria-current={atual === chave ? "page" : undefined}
      className={`inline-flex min-h-11 items-center rounded-full px-4 text-sm font-semibold transition ${
        atual === chave
          ? "bg-laranja text-azul"
          : "text-navy-mute hover:bg-navy-ink/10 hover:text-navy-ink"
      }`}
    >
      {rotulo}
    </Link>
  );

  return (
    <header className="sticky top-0 z-40 bg-azul text-navy-ink shadow-[0_4px_18px_-8px_rgba(5,13,28,0.5)]">
      <div className="mx-auto flex max-w-6xl items-center gap-4 px-4 py-3 sm:px-6">
        <Link to="/" aria-label="Início">
          <LogoCfFbc className="h-8" />
        </Link>
        <nav className="ml-auto flex items-center gap-1">
          {item("inicio", "Início", "/")}
          {item("faculdade", "Faculdade", "/faculdade")}
          <MenuConta nome={nome} avatarUrl={avatarUrl} />
        </nav>
      </div>
    </header>
  );
}

function MenuConta({ nome, avatarUrl }: { nome: string | null; avatarUrl?: string | null }) {
  // A porta para o painel de operação aparece só para quem tem papel. Não é
  // segurança — é para não oferecer ao aluno um link que o banco vai recusar.
  // Custa uma chamada de `meus_papeis()` por carregamento de tela; para 59 mil
  // alunos ela devolve array vazio por uma busca na chave primária.
  const operador = useOperador();
  const ehOperador = operador.fase === "pronto" && operador.operador.painel;
  const temConsultoria = useTemConsultoria();

  return (
    <details className="relative ml-1">
      {/* As iniciais não são nome acessível: quem usa leitor de tela ouviria
          "MH". O `aria-label` diz o que o controle faz; o `title` fica para o
          mouse. */}
      <summary
        aria-label="Minha conta"
        className="grid size-11 cursor-pointer list-none place-items-center overflow-hidden rounded-full bg-laranja font-display text-sm font-bold text-azul marker:hidden"
        title={nome ?? "Minha conta"}
      >
        {avatarUrl ? (
          // `alt` vazio de propósito: o `aria-label` do <summary> já nomeia o
          // controle, e "foto de Victor" ali seria o nome lido duas vezes.
          <img src={avatarUrl} alt="" className="size-full object-cover" />
        ) : (
          iniciais(nome)
        )}
      </summary>
      <div className="surface absolute right-0 z-50 mt-2 w-64 p-2 text-tinta">
        {nome && (
          <p className="truncate px-3 py-2 text-sm font-semibold" title={nome}>
            {nome}
          </p>
        )}

        <Link
          to="/perfil"
          className="flex min-h-11 items-center gap-2 rounded-lg px-3 text-sm font-medium text-cinza transition hover:bg-laranja/10 hover:text-titulo"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="size-4" aria-hidden="true">
            <circle cx="12" cy="8" r="4" />
            <path d="M5 21v-1a7 7 0 0 1 14 0v1" />
          </svg>
          Meu perfil
        </Link>

        {/* O acervo entra no MENU e não numa aba de curso porque ele sobrevive
            ao curso (000281): quem se formou não tem mais aba nenhuma, e é
            justamente quem mais precisa achar o certificado. */}
        <Link
          to="/meus-documentos"
          className="flex min-h-11 items-center gap-2 rounded-lg px-3 text-sm font-medium text-cinza transition hover:bg-laranja/10 hover:text-titulo"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="size-4" aria-hidden="true">
            <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
            <path d="M14 3v5h5" />
          </svg>
          Meus documentos
        </Link>

        {/* As pesquisas de satisfação (000310): no MENU pelo mesmo motivo do
            acervo — a pesquisa é do curso, e quem responde é a pessoa. */}
        <Link
          to="/avaliacoes"
          className="flex min-h-11 items-center gap-2 rounded-lg px-3 text-sm font-medium text-cinza transition hover:bg-laranja/10 hover:text-titulo"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="size-4" aria-hidden="true">
            <path d="M12 3l2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.9 1-6.1L3.2 9.5l6.1-.9z" />
          </svg>
          Avaliações
        </Link>

        {/* As consultorias do portal antigo (000351): a porta só aparece para quem
            tem chamado — são 280 pessoas, e um link para uma lista vazia seria
            ruído no menu dos outros 60 mil. */}
        {temConsultoria && (
          <Link
            to="/consultorias"
            className="flex min-h-11 items-center gap-2 rounded-lg px-3 text-sm font-medium text-cinza transition hover:bg-laranja/10 hover:text-titulo"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="size-4" aria-hidden="true">
              <path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12z" />
            </svg>
            Minhas consultorias
          </Link>
        )}

        {ehOperador && (
          <Link
            to="/admin/painel"
            className="flex min-h-11 items-center gap-2 rounded-lg px-3 text-sm font-medium text-cinza transition hover:bg-laranja/10 hover:text-titulo"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="size-4" aria-hidden="true">
              <rect x="3" y="3" width="7" height="9" rx="1.5" />
              <rect x="14" y="3" width="7" height="5" rx="1.5" />
              <rect x="14" y="12" width="7" height="9" rx="1.5" />
              <rect x="3" y="16" width="7" height="5" rx="1.5" />
            </svg>
            Painel de operação
          </Link>
        )}

        <EscolhaDeTema />

        <button
          onClick={() => void supabase.auth.signOut()}
          className="min-h-11 w-full rounded-lg px-3 text-left text-sm font-medium text-cinza transition hover:bg-laranja/10 hover:text-titulo"
        >
          Sair da minha conta
        </button>
      </div>
    </details>
  );
}

/**
 * Claro, escuro ou o do aparelho.
 *
 * Três opções e não um interruptor de dois estados: "sistema" é o padrão, e é
 * diferente de "claro" — quem não escolheu acompanha o aparelho, o que no
 * celular quer dizer acompanhar o modo noturno automático. Um toggle de dois
 * estados obrigaria a escolher, e tiraria esse comportamento de quem já o tinha.
 */
function EscolhaDeTema() {
  const { tema, definir } = useTema();
  const opcoes: Array<{ id: Tema; rotulo: string; icone: ReactNode }> = [
    { id: "claro", rotulo: "Claro", icone: <IconeSol /> },
    { id: "escuro", rotulo: "Escuro", icone: <IconeLua /> },
    { id: "sistema", rotulo: "Automático", icone: <IconeSistema /> },
  ];

  return (
    <div className="my-1 border-y border-borda py-2">
      <p className="px-3 pb-1.5 text-xs font-semibold tracking-wide text-cinza uppercase">
        Aparência
      </p>
      <div role="radiogroup" aria-label="Aparência" className="flex gap-1 px-1">
        {opcoes.map((o) => (
          <button
            key={o.id}
            role="radio"
            aria-checked={tema === o.id}
            onClick={() => definir(o.id)}
            title={o.rotulo}
            className={`flex min-h-11 flex-1 flex-col items-center justify-center gap-1 rounded-lg px-1 py-1.5 text-[0.68rem] font-semibold transition ${
              tema === o.id
                ? "bg-laranja text-azul"
                : "text-cinza hover:bg-laranja/10 hover:text-titulo"
            }`}
          >
            {o.icone}
            {o.rotulo}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Alternador de tema para telas sem menu de conta (login, redefinição).
 *
 * Aqui é um botão só, e não os três estados do menu: quem chega no login quer
 * clarear ou escurecer o que está vendo, não configurar preferência. O clique
 * troca para o oposto do que está na tela, o que também resolve o caso de quem
 * está em "automático" — sai do automático para o que ele escolheu agora.
 */
export function BotaoTema() {
  const { efetivo, definir } = useTema();
  const irPara = efetivo === "escuro" ? "claro" : "escuro";
  return (
    <button
      type="button"
      onClick={() => definir(irPara)}
      aria-label={irPara === "escuro" ? "Mudar para o modo escuro" : "Mudar para o modo claro"}
      title={irPara === "escuro" ? "Modo escuro" : "Modo claro"}
      className="inline-flex size-11 items-center justify-center rounded-full text-navy-mute transition hover:bg-navy-ink/10 hover:text-navy-ink"
    >
      {efetivo === "escuro" ? <IconeSol /> : <IconeLua />}
    </button>
  );
}

function IconeSol() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="size-4" aria-hidden="true">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </svg>
  );
}

function IconeLua() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="size-4" aria-hidden="true">
      <path d="M20 14.5A8.5 8.5 0 0 1 9.5 4a7 7 0 1 0 10.5 10.5z" />
    </svg>
  );
}

function IconeSistema() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="size-4" aria-hidden="true">
      <rect x="2" y="4" width="20" height="13" rx="2" />
      <path d="M8 21h8M12 17v4" />
    </svg>
  );
}

/** Tela de aviso centrada — erro, sem vínculo, link expirado. */
export function Aviso({
  titulo,
  children,
  acao,
}: {
  titulo: string;
  children: ReactNode;
  acao?: ReactNode;
}) {
  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-10">
      <div className="surface w-full max-w-md p-7 text-center">
        <h1 className="text-xl font-bold text-titulo">{titulo}</h1>
        <div className="mt-3 text-sm leading-relaxed text-cinza">{children}</div>
        {acao && <div className="mt-5 flex justify-center">{acao}</div>}
      </div>
    </div>
  );
}

// O loader da marca mora em `componentes/Carregando.tsx` — símbolo do CF entre
// arcos que giram. Reexportado aqui para quem já importava daqui.
export { default as Carregando } from "./Carregando";

/** As iniciais do aluno, para quando não há foto. Reexportado de `lib/dados`
 *  porque a tela de perfil precisa do mesmo cálculo do avatar do header. */
export { iniciais as iniciaisDe } from "../lib/dados";

/**
 * A marca de quem oferece o curso: escudo da FBC ou símbolo do CF.
 *
 * Os dois são os arquivos oficiais, não desenho aproximado — o escudo saiu
 * recortado do logo da FBC (`central-professores/src/assets/fbc-logo.png`) e o
 * símbolo do CF é o mesmo do favicon. Ambos com fundo transparente, então valem
 * nos dois temas: o escudo tem contorno laranja e o símbolo é laranja cheio.
 *
 * `object-contain` num box quadrado, porque as duas formas têm proporções
 * diferentes (o escudo é mais alto que largo) e cortar marca é pior que sobrar
 * espaço.
 */
export function MarcaDoCurso({
  faculdade,
  className = "size-11",
}: {
  faculdade: boolean;
  className?: string;
}) {
  const base = `${className} shrink-0 object-contain`;

  // O símbolo do CF é laranja cheio: legível sobre claro e sobre escuro.
  if (!faculdade) {
    return <img src={simboloCf} alt="" aria-hidden="true" className={base} />;
  }

  // O escudo tem miolo azul-marinho, que desaparece sobre fundo escuro — daí as
  // duas versões, trocadas pelo tema. Duas `<img>` e não um filtro CSS: filtro
  // sobre logo colorido estraga o laranja da marca.
  return (
    <>
      <img src={escudoFbc} alt="" aria-hidden="true" className={`${base} dark:hidden`} />
      <img
        src={escudoFbcBranco}
        alt=""
        aria-hidden="true"
        className={`${base} hidden dark:block`}
      />
    </>
  );
}

/**
 * O escudo da FBC sobre superfície escura — a faixa da faculdade na tela inicial
 * e a área `/faculdade`, que são navy nos dois temas.
 *
 * Substituiu o monograma "FBC" em círculo de dupla borda que existia aqui: ele
 * era um substituto por falta de asset, e o asset existe — saiu recortado do logo
 * oficial (`central-professores/src/assets/fbc-logo-branco.png`). Logo de verdade
 * comunica mais que monograma bem-feito.
 */
export function EscudoFBC({ className = "size-14" }: { className?: string }) {
  return (
    <img
      src={escudoFbcBranco}
      alt="FBC — Faculdade Brasileira de Contabilidade"
      className={`${className} shrink-0 object-contain`}
    />
  );
}

// O monograma "FBC" em círculo de dupla borda que existia aqui foi removido: era
// um substituto por falta de asset (os vetores da FBC estão no Figma sob
// permissão), e o asset apareceu — `EscudoFBC` usa o logo oficial recortado. Não
// vale manter as duas formas de dizer a mesma coisa: a segunda entra em uma tela
// por descuido e a faculdade passa a ter duas marcas.
